import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalStorageState } from '@/hooks/ai-agents/useLocalStorageState';
import { useUniversalLibraryItemClick } from '@/hooks/universal-storage/useUniversalLibraryItemClick';
import { useToast } from '@/hooks/errors/use-toast';
import type { SavedRecommendation } from '@/types/recommendation-tracking';

/**
 * Hook for handling View button navigation for all recommendation types
 * Implements the standard pattern: close chat modal → navigate to target
 */
export const useViewRecommendationNavigation = () => {
  const navigate = useNavigate();
  const { isModalOpen, closeModal } = useLocalStorageState();
  const { setLightboxState } = useUniversalLibraryItemClick();
  const { toast } = useToast();

  // Helper to close chat modal before navigation
  const closeModalIfOpen = useCallback(() => {
    if (isModalOpen) {
      closeModal();
    }
  }, [isModalOpen, closeModal]);

  // View Task: Navigate to personal task manager
  const viewTask = useCallback((recommendation: SavedRecommendation) => {
    console.log('[ViewNavigation] 🔍 Navigating to personal task:', {
      id: recommendation.id,
      type: recommendation.type,
      savedAt: recommendation.savedAt,
      metadata: recommendation.metadata
    });
    
    // Use the recommendation.id which is the saved task ID
    const taskId = recommendation.id;
    
    // Navigate to the page
    navigate('/task-manager');
    
    // Close the modal
    closeModalIfOpen();
    
    // Dispatch custom event to open the task after navigation
    if (taskId) {
      console.log('[ViewNavigation] Dispatching open-task-detail event:', taskId);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('open-task-detail', {
          detail: { taskId }
        }));
      }, 300); // Small delay to ensure page has loaded
    }
  }, [navigate, closeModalIfOpen]);

  // View Document: Navigate to private text generator
  const viewPage = useCallback((recommendation: SavedRecommendation) => {
    console.log('[ViewNavigation] 🔍 Navigating to private text library:', {
      id: recommendation.id,
      type: recommendation.type,
      metadata: recommendation.metadata
    });
    
    // Use the recommendation.id which is the saved page ID
    const pageId = recommendation.id;
    
    // Navigate to the page
    navigate('/private-text-generator');
    
    // Close the modal
    closeModalIfOpen();
    
    // Dispatch custom event to open the page after navigation
    if (pageId) {
      console.log('[ViewNavigation] Dispatching open-page-detail event:', pageId);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('open-page-detail', {
          detail: { pageId }
        }));
      }, 300); // Small delay to ensure page has loaded
    }
  }, [navigate, closeModalIfOpen]);

  // View Research: Navigate to private research generator
  const viewResearch = useCallback((recommendation: SavedRecommendation) => {
    console.log('[ViewNavigation] 🔍 Navigating to private research library:', {
      id: recommendation.id,
      type: recommendation.type,
      metadata: recommendation.metadata
    });
    
    // Use the recommendation.id which is the saved research ID
    const researchId = recommendation.id;
    
    // Navigate to the page
    navigate('/private-research-generator');
    
    // Close the modal
    closeModalIfOpen();
    
    // Dispatch custom event to open the research after navigation
    if (researchId) {
      console.log('[ViewNavigation] Dispatching open-research-detail event:', researchId);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('open-research-detail', {
          detail: { researchId }
        }));
      }, 300); // Small delay to ensure page has loaded
    }
  }, [navigate, closeModalIfOpen]);

  // View Image: Navigate to private image generator with auto-open
  const viewImage = useCallback((recommendation: SavedRecommendation) => {
    console.log('[ViewNavigation] 🔍 Navigating to private image library:', {
      id: recommendation.id,
      type: recommendation.type,
      metadata: recommendation.metadata,
      savedImageId: recommendation.metadata?.savedImageId
    });
    
    // Use the recommendation.id which is the saved image ID
    const imageId = recommendation.id;
    
    // Navigate to the page
    navigate('/private-image-generator');
    
    // Close the modal
    closeModalIfOpen();
    
    // Dispatch custom event to open the image after navigation
    if (imageId) {
      console.log('[ViewNavigation] Dispatching open-image-lightbox event:', imageId);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('open-image-lightbox', {
          detail: { imageId }
        }));
      }, 300); // Small delay to ensure page has loaded
    }
  }, [navigate, closeModalIfOpen]);

  // Generic view handler that routes to the appropriate function
  const viewRecommendation = useCallback((recommendation: SavedRecommendation) => {
    switch (recommendation.type) {
      case 'task':
        return viewTask(recommendation);
      case 'page':
        return viewPage(recommendation);
      case 'research':
        return viewResearch(recommendation);
      case 'image':
        return viewImage(recommendation);
      default:
        console.error('[ViewNavigation] Unknown recommendation type:', recommendation.type);
    }
  }, [viewTask, viewPage, viewResearch, viewImage]);

  return {
    viewTask,
    viewPage,
    viewResearch,
    viewImage,
    viewRecommendation
  };
}; 